import { useEffect, useRef, useState, type FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createPost, updatePost, uploadAsset } from '../../components/shared/blog/blog-api';
import type { Post, PostInput } from '../../components/shared/blog/contracts/post';

interface PostEditorProps {
  /** The post being edited, or null when creating a new one. */
  initial: Post | null;
  getToken: () => Promise<string>;
  onSaved: (post: Post) => void;
  onCancel: () => void;
}

/** Derive a URL slug from a title (lowercase, hyphenated, alnum only). */
function slugify(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function PostEditor({ initial, getToken, onSaved, onCancel }: PostEditorProps) {
  const isNew = initial === null;
  const [title, setTitle] = useState(initial?.title ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [excerpt, setExcerpt] = useState(initial?.excerpt ?? '');
  const [tags, setTags] = useState((initial?.tags ?? []).join(', '));
  const [content, setContent] = useState(initial?.content ?? '');

  const [saving, setSaving] = useState<false | 'draft' | 'published'>(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const dirty = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Auto-derive the slug from the title until the user edits it (new posts only).
  useEffect(() => {
    if (isNew && !slugTouched) setSlug(slugify(title));
  }, [title, slugTouched, isNew]);

  // Warn on navigating away with unsaved changes.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const markDirty = () => {
    dirty.current = true;
  };

  async function save(status: 'draft' | 'published', e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    setSaving(status);
    const tagList = tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const input: PostInput = {
      title: title.trim(),
      excerpt: excerpt.trim(),
      tags: tagList,
      content,
      status,
    };

    try {
      const token = await getToken();
      const saved = isNew
        ? await createPost(token, { ...input, slug: slug || undefined })
        : await updatePost(token, initial!.slug, input);
      dirty.current = false;
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
      setSaving(false);
    }
  }

  async function handleImage(file: File) {
    setError(null);
    setUploading(true);
    try {
      const token = await getToken();
      const { url } = await uploadAsset(token, file);
      // Append a markdown image reference at the end of the body.
      setContent((c) => `${c}\n\n![${file.name}](${url})\n`);
      markDirty();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Image upload failed.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    // Submit is a no-op: publishing is an explicit button action so pressing
    // Enter in a text field can't accidentally publish a draft.
    <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-xl font-bold text-fg">
          {isNew ? 'New post' : `Editing: ${initial!.slug}`}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="font-mono text-sm text-muted hover:text-accent"
        >
          ← back to list
        </button>
      </div>

      {error && (
        <p role="alert" className="font-mono text-sm text-red-400">
          {error}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="post-title" className="font-mono text-sm text-muted">
            title
          </label>
          <input
            id="post-title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              markDirty();
            }}
            required
            className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm text-fg outline-none focus:border-accent"
          />
        </div>
        <div>
          <label htmlFor="post-slug" className="font-mono text-sm text-muted">
            slug {isNew ? '(auto from title)' : '(fixed)'}
          </label>
          <input
            id="post-slug"
            value={slug}
            disabled={!isNew}
            onChange={(e) => {
              const next = slugify(e.target.value);
              setSlug(next);
              // Clearing the field resumes auto-derive from the title.
              setSlugTouched(next.length > 0);
              markDirty();
            }}
            className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm text-fg outline-none focus:border-accent disabled:opacity-60"
          />
        </div>
      </div>

      <div>
        <label htmlFor="post-excerpt" className="font-mono text-sm text-muted">
          excerpt
        </label>
        <input
          id="post-excerpt"
          value={excerpt}
          onChange={(e) => {
            setExcerpt(e.target.value);
            markDirty();
          }}
          className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm text-fg outline-none focus:border-accent"
        />
      </div>

      <div>
        <label htmlFor="post-tags" className="font-mono text-sm text-muted">
          tags (comma-separated)
        </label>
        <input
          id="post-tags"
          value={tags}
          onChange={(e) => {
            setTags(e.target.value);
            markDirty();
          }}
          className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm text-fg outline-none focus:border-accent"
        />
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label htmlFor="post-body" className="font-mono text-sm text-muted">
            body (markdown)
          </label>
          <label className="cursor-pointer font-mono text-xs text-accent hover:underline">
            {uploading ? 'uploading…' : '+ image'}
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleImage(f);
              }}
            />
          </label>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <textarea
            id="post-body"
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              markDirty();
            }}
            rows={20}
            className="w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm leading-relaxed text-fg outline-none focus:border-accent"
          />
          <div className="terminal-card overflow-auto p-4" aria-label="Live preview">
            <div className="prose-post">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content || '_Preview appears here…_'}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => save('published')}
          disabled={saving !== false}
          className="rounded-md bg-accent px-4 py-2 font-mono text-sm font-medium text-bg transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {saving === 'published' ? 'publishing…' : 'publish'}
        </button>
        <button
          type="button"
          onClick={() => save('draft')}
          disabled={saving !== false}
          className="tag disabled:opacity-50"
        >
          {saving === 'draft' ? 'saving…' : 'save draft'}
        </button>
      </div>
    </form>
  );
}
